import { Button, Card, Form, Modal, Table } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';

import { faSave } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import httpRequest from 'src/services/httpRequest';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api, getUserFromUserId } from 'src/models/Api.model';
import { Appointment } from 'src/models/Appointment.model';
import { Disease, DiseaseFullName } from 'src/models/Disease.model';
import { Eye } from 'src/models/Eye.model';
import { DeviceDataGetRequestBody } from 'src/models/GalenData/DeviceDataGetRequestBody.model';
import {
  DeviceDataView,
  ExamData,
  GradingDetail,
  GradingResults,
} from 'src/models/GalenData/DeviceDataView.model';
import { ApiUrlsEnum } from 'src/models/GalenData/Request.model';
import { SortOrder, SortOrderEnum } from 'src/models/GalenData/SortOrder.model';
import { Gender } from 'src/models/Gender.model';
import {
  FindGrading,
  Grading,
  GradingIsPositive,
  GradingName,
} from 'src/models/Grading.model';
import { getLocale, getLocaleCode, Language } from 'src/models/Language.model';
import { ModelEnum } from 'src/models/Model.model';
import { ReportTemplate } from 'src/models/ReportTemplate.model';
import { Result } from 'src/models/Result.model';
import { Session } from 'src/models/Session.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetRetinalExamDataModelId from 'src/hooks/useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

import { getMatchedPatient } from 'src/utils/patientMatching';
import { namePattern, pidPattern } from 'src/utils/validationRules';

import { defaultPageSize } from './OHResults/OHResults';

interface IReportEditorProps {
  currentPage: number;
  currentSearchValue: string;
  handleLoadSessions: (
    page: number,
    pageSize: number,
    searchValue: string,
    sessionsSortOrder: SortOrder,
  ) => Promise<void>;
  show: boolean;
  session: Session;
  setShow: (showEditor: boolean) => void;
}

const selectedClass = 'fw-bold';
const unselectedClass = '';
const negativeClass = '';
const positiveClass = 'text-warning';
const ungradableClass = 'text-warning';

const updateResults = (results: GradingResults, data: Result[]) => {
  const diseases = ['amd', 'dr', 'glaucoma'];

  for (const [key, detail] of Object.entries(results) as [
    ModelEnum,
    GradingDetail,
  ][]) {
    for (const disease of diseases) {
      if (key.toLowerCase().startsWith(disease)) {
        const result = data.find((item) => item.disease === disease);
        if (result) {
          detail.grading = result.gradable
            ? result.grading.toString()
            : Grading.UNGRADEABLE;
        }
      }
    }
  }
};

const ReportEditor = ({
  show,
  session,
  setShow,
  currentPage,
  currentSearchValue,
  handleLoadSessions,
}: IReportEditorProps) => {
  const { tCommon, tPages, language } = useUiTranslation();
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, dirtyFields, isSubmitting, isValid },
  } = useForm({
    defaultValues: {
      ...session,
      appointment: session.appointment ?? Appointment.NA,
      reportDatetime: `${session.date} ${session.time}`,
    },

    mode: 'all',
  });

  const { userId, fullName, userEmail } = useGetCurrentUser();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const {
    deviceDataModelId,
    examSubmissionPropertySetId,
    examReportPropertySetId,
    followupPropertySetId,
  } = useGetRetinalExamDataModelId();

  const handlePatientInformationUpdate = async (
    data: Session,
    currentExam: AxiosResponse<DeviceDataView<ExamData>>,
  ) => {
    const patientFields: (keyof Session)[] = ['firstName', 'lastName', 'id'];
    if (!patientFields.some((field) => data[field] !== session[field])) {
      return;
    }

    if (!currentExam.data.ownerId) {
      throw new Error(tCommon('message.error.patientIdNotFound'));
    }

    const matchedPatients = await getMatchedPatient({
      firstName: data.firstName,
      lastName: data.lastName,
      dob: data.dob,
      id: data.id,
    });

    if (matchedPatients.length > 0) {
      throw new Error(tCommon('patientFields.patientAlreadyExists'));
    }

    const patient = await getUserFromUserId(currentExam.data.ownerId);
    if (!patient) {
      throw new Error(tCommon('message.error.patientNotFound'));
    }

    patient.firstName = data.firstName;
    patient.lastName = data.lastName;
    patient.patientId = data.id;

    await Api.remote._update_user_custom_fields([], patient);
  };

  const handleExamReportUpdate = async (
    data: Session,
    currentExam: AxiosResponse<DeviceDataView<ExamData>>,
  ) => {
    if (!userId) {
      throw new Error(tCommon('message.error.userIdNotFound'));
    }
    const examReportFields: (keyof Session)[] = ['rightResults', 'leftResults'];
    if (
      !examReportFields.some(
        (field) =>
          JSON.stringify(data[field]) !== JSON.stringify(session[field]),
      )
    ) {
      return;
    }

    const leftEyeModels = currentExam.data.data.LeftEyeResultJson.value.models;
    const leftEyeResults =
      currentExam.data.data.LeftEyeResultJson.value.results;
    const rightEyeModels =
      currentExam.data.data.RightEyeResultJson.value.models;
    const rightEyeResults =
      currentExam.data.data.RightEyeResultJson.value.results;

    const oldLeftEyeResults = structuredClone(leftEyeResults);
    const oldRightEyeResults = structuredClone(rightEyeResults);

    updateResults(leftEyeResults, data.leftResults ?? []);
    updateResults(rightEyeResults, data.rightResults ?? []);

    await Api.remote._save_device_data_record({
      data: {
        LeftEyeResultJson: {
          models: leftEyeModels,
          results: leftEyeResults,
        },
        RightEyeResultJson: {
          models: rightEyeModels,
          results: rightEyeResults,
        },
      },
      deviceDataId: session.iid,
      deviceDataModelId,
      devicePropertySetId: examReportPropertySetId,
    });

    if (data.history && data.history.length === 0) {
      const originalRecord = {
        PracticeUserId: currentExam.data.data.PracticeUserId.value,
        SavedTime: new Date(data.date + ' ' + data.time),
        LeftEyeResultJson: {
          models: leftEyeModels,
          results: oldLeftEyeResults,
        },
        RightEyeResultJson: {
          models: rightEyeModels,
          results: oldRightEyeResults,
        },
      };
      data.history.push(originalRecord);
    }

    const currentRecord = {
      PracticeUserId: userId,
      UserFullname: fullName,
      UserEmail: userEmail,
      SavedTime: new Date(),
      LeftEyeResultJson: {
        models: leftEyeModels,
        results: leftEyeResults,
      },
      RightEyeResultJson: {
        models: rightEyeModels,
        results: rightEyeResults,
      },
    };
    data.history?.push(currentRecord);
  };

  const handleExamSubmissionUpdate = async (
    data: Session & { reportDatetime: string },
  ) => {
    await Api.remote._save_device_data_record({
      data: {
        ReportDatetime: moment(data.reportDatetime),
        History: data.history,
      },
      deviceDataId: session.iid,
      deviceDataModelId,
      devicePropertySetId: examSubmissionPropertySetId,
    });
  };

  const handleFollowupUpdate = async (data: Session) => {
    const followupFields: (keyof Session)[] = ['notes', 'appointment'];
    if (!followupFields.some((field) => data[field] !== session[field])) {
      return;
    }

    await Api.remote._save_device_data_record({
      data: {
        Notes: data.notes,
        AppointmentType: data.appointment,
      },
      deviceDataId: session.iid,
      deviceDataModelId,
      devicePropertySetId: followupPropertySetId,
    });
  };

  const onSubmit = async (data: Session & { reportDatetime: string }) => {
    try {
      const currentExam = await httpRequest.get<
        DeviceDataView<ExamData>,
        AxiosResponse<DeviceDataView<ExamData>>,
        DeviceDataGetRequestBody
      >(`${ApiUrlsEnum.DEVICE_DATA_OWNER}/${deviceDataModelId}/${data.iid}`);

      await handlePatientInformationUpdate(data, currentExam);
      await handleExamReportUpdate(data, currentExam);
      await handleExamSubmissionUpdate(data);
      await handleFollowupUpdate(data);

      handleLoadSessions(
        currentPage - 1,
        defaultPageSize,
        currentSearchValue,
        SortOrderEnum.DESC,
      );
      Api.alertBox({
        title: tCommon('bootbox.title.Success'),
        message: tCommon('reportEditor.reportSavedSuccess'),
        locale: getLocaleCode(language as Language),
        type: 'success',
      });
      setShow(false);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: `${tCommon('reportEditor.reportSavedError')} ${errorMessage}`,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  const gradingOptions = (
    eye: Eye,
    disease: Disease,
    template?: ReportTemplate,
  ) => {
    const gradingValues: Partial<{ [key in Disease]: Grading[] }> = {
      [Disease.AMD]: [
        Grading.NEGATIVE_OR_EARLY,
        Grading.INTERMEDIATE,
        Grading.LATE_DRY,
        Grading.LATE_WET,
      ],
      [Disease.DR]:
        template === ReportTemplate.OPTAIN_SMALL_DR
          ? [Grading.NEGATIVE, Grading.MTMDR, Grading.VTDR] // US-DR
          : [
              Grading.NO_DR,
              Grading.MILD_NPDR,
              Grading.MODERATE_NPDR,
              Grading.SEVERE_NPDR,
              Grading.PROLIFERATIVE_DR,
            ],
      [Disease.GLAUCOMA]: [
        Grading.LOW_RISK,
        Grading.MODERATE_RISK,
        Grading.HIGH_RISK,
      ],
    };

    const resultsKey = eye === Eye.LEFT ? 'leftResults' : 'rightResults';
    const results = watch(resultsKey);
    const diseaseIndex = results?.findIndex((item) => item.disease === disease);

    return results && diseaseIndex !== undefined && diseaseIndex >= 0 ? (
      <>
        {gradingValues[disease]?.map((grading) => (
          <Form.Check
            key={grading}
            type="radio"
            id={`${eye}-${disease}-${grading}`}
            label={tCommon(`gradingName.${GradingName(grading)}`)}
            value={grading}
            onClick={() => {
              setValue(`${resultsKey}.${diseaseIndex}.gradable`, true, {
                shouldDirty: true,
              });
            }}
            className={
              FindGrading(results, disease) === grading
                ? `${selectedClass} ` +
                  (GradingIsPositive(FindGrading(results, disease) as Grading)
                    ? positiveClass
                    : negativeClass)
                : unselectedClass
            }
            {...register(`${resultsKey}.${diseaseIndex}.grading`)}
          />
        ))}

        <Form.Check
          type="radio"
          id={`${eye}-${disease}-ungradable`}
          label={tCommon('gradingName.ungradable')}
          className={
            results[diseaseIndex].gradable
              ? unselectedClass
              : `${selectedClass} ${ungradableClass}`
          }
          checked={!results[diseaseIndex].gradable}
          onChange={(el) => {
            if (el.target.checked) {
              setValue(`${resultsKey}.${diseaseIndex}.gradable`, false, {
                shouldDirty: true,
              });
              setValue(`${resultsKey}.${diseaseIndex}.grading`, Grading.EMPTY);
            }
          }}
        />
      </>
    ) : (
      <></>
    );
  };

  const appointment = watch('appointment');

  return (
    <CustomModal
      show={show}
      onHide={handleClose}
      centered
      size="xl"
      scrollable={true}
      fullscreen="xl-down"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ height: '55px' }}>
          <span style={{ lineHeight: '55px' }}>
            {tCommon('reportEditor.editReport')}
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ width: '100%', height: '100vh' }}>
        <Card className="mb-4">
          <Card.Body className="py-2">
            <Table className="mb-0">
              <tbody>
                <tr>
                  <td className="w-20">{tCommon('patientFields.dateTime')}</td>
                  <td colSpan={2}>
                    <Controller
                      control={control}
                      {...register('reportDatetime', {
                        required: tCommon('patientFields.dateTimeRequired'),
                      })}
                      render={({ field: { onChange, value } }) => (
                        <DatePicker
                          locale={getLocale(language as Language)}
                          autoComplete="off"
                          onChange={onChange}
                          calendarClassName="custom-datepicker"
                          wrapperClassName="w-100"
                          className="form-control"
                          placeholderText={tCommon(
                            'patientFields.dateTimePlaceholder',
                          )}
                          dateFormat="yyyy-MM-dd HH:mm:ss"
                          maxDate={new Date()}
                          showMonthDropdown
                          showYearDropdown
                          yearDropdownItemNumber={150}
                          showTimeSelect
                          scrollableYearDropdown
                          timeIntervals={5}
                          selected={value ? moment(value).toDate() : undefined}
                          filterTime={(time) => {
                            return time <= new Date();
                          }}
                        />
                      )}
                    />
                    {errors.reportDatetime?.message && (
                      <p className="text-danger">
                        {errors.reportDatetime.message}
                      </p>
                    )}
                  </td>
                </tr>

                <tr>
                  <td>{tCommon('patientFields.patientID')}</td>
                  <td colSpan={2}>
                    <Form.Control
                      type="number"
                      placeholder={tCommon('patientFields.pidPlaceholder')}
                      autoComplete="off"
                      {...register('id', {
                        required: tCommon('patientFields.pidRequired'),
                        maxLength: {
                          value: 20,
                          message: tCommon('patientFields.pidMaxLength'),
                        },
                        pattern: {
                          value: pidPattern,
                          message: tCommon('patientFields.pidInvalid'),
                        },
                      })}
                    />

                    {errors.id?.message && (
                      <p className="text-danger">{errors.id?.message}</p>
                    )}
                  </td>
                </tr>

                <tr>
                  <td>{tCommon('patientFields.patientName')}</td>
                  <td>
                    <Form.Control
                      placeholder={tCommon(
                        'patientFields.firstNamePlaceholder',
                      )}
                      autoComplete="off"
                      {...register('firstName', {
                        required: tCommon('patientFields.firstNameRequired'),
                        maxLength: {
                          value: 100,
                          message: tCommon('patientFields.firstNameMaxLength'),
                        },
                        pattern: {
                          value: namePattern,
                          message: tCommon('patientFields.firstNameInvalid'),
                        },
                      })}
                    />

                    {errors.firstName?.message && (
                      <p className="text-danger">{errors.firstName?.message}</p>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      placeholder={tCommon('patientFields.lastNamePlaceholder')}
                      autoComplete="off"
                      {...register('lastName', {
                        required: tCommon('patientFields.lastNameRequired'),
                        maxLength: {
                          value: 100,
                          message: tCommon('patientFields.lastNameMaxLength'),
                        },
                        pattern: {
                          value: namePattern,
                          message: tCommon('patientFields.lastNameInvalid'),
                        },
                      })}
                    />
                    {errors.lastName?.message && (
                      <p className="text-danger">{errors.lastName?.message}</p>
                    )}
                  </td>
                </tr>

                <tr>
                  <td className="border-0">{tCommon('patientFields.DOB')}</td>
                  <td colSpan={2} className="border-0">
                    <Form.Control
                      type="text"
                      disabled
                      readOnly
                      placeholder={tCommon('patientFields.dobPlaceholder')}
                      {...register('dob')}
                    />
                  </td>
                </tr>

                {session.template?.includes('cvd') && (
                  <tr>
                    <td className="border-top border-bottom-0">
                      {tCommon('patientFields.sex')}
                    </td>
                    <td colSpan={2} className="border-top border-bottom-0">
                      <Form.Control
                        disabled
                        readOnly
                        as="select"
                        {...register('gender')}
                      >
                        {Object.values(Gender).map((gender) => (
                          <option key={gender} value={gender}>
                            {tCommon(`patientFields.${gender}`)}
                          </option>
                        ))}
                      </Form.Control>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Body className="py-2">
            <Table className="mb-0">
              <thead>
                <tr>
                  <th className="w-20">{tCommon('patientFields.disease')}</th>
                  <th className="w-40">{tCommon('patientFields.rightEye')}</th>
                  <th className="w-40">{tCommon('patientFields.leftEye')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td></td>
                  <td className="text-center align-middle">
                    {session.rightImageFile ? (
                      <img
                        src={session.rightImageFile}
                        className="img-fluid rounded"
                        alt={tPages('newExam.rightEye')}
                      />
                    ) : (
                      <p className="text-center align-middle">
                        <p className="p lead text-white m-0">
                          {tCommon('patientFields.noImg')}
                        </p>
                      </p>
                    )}
                  </td>
                  <td className="text-center align-middle">
                    {session.leftImageFile ? (
                      <img
                        src={session.leftImageFile}
                        className="img-fluid rounded"
                        alt={tPages('newExam.leftEye')}
                      />
                    ) : (
                      <p className="text-center align-middle">
                        <p className="p lead text-white m-0">
                          {tCommon('patientFields.noImg')}
                        </p>
                      </p>
                    )}
                  </td>
                </tr>

                {(session.template === ReportTemplate.OPTAIN_SMALL_DR
                  ? [Disease.DR]
                  : [Disease.GLAUCOMA, Disease.DR, Disease.AMD]
                ).map((disease) => (
                  <tr key={disease}>
                    <td className="border-top border-bottom-0">
                      {tCommon(`diseaseFullName.${DiseaseFullName(disease)}`)}
                    </td>
                    <td className="border-top border-bottom-0">
                      {gradingOptions(Eye.RIGHT, disease, session.template)}
                    </td>
                    <td className="border-top border-bottom-0">
                      {gradingOptions(Eye.LEFT, disease, session.template)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Body className="py-2">
            <Table className="mb-0">
              <tbody>
                <tr>
                  <td className="w-25">
                    {tCommon('reportEditor.clinicianNotes')}
                  </td>
                  <td className="w-75">
                    <Form.Control
                      as="textarea"
                      data-test="clinical-notes-textarea"
                      style={{ height: '120px' }}
                      placeholder={tCommon(
                        'reportEditor.clinicianNotesPlaceholder',
                      )}
                      value={watch('notes')}
                      onChange={(el) => {
                        const value = el.target.value;
                        setValue('notes', value || undefined, {
                          shouldDirty: true,
                        });
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border-bottom-0">
                    {tCommon('reportEditor.nextAppointment')}
                  </td>
                  <td className="border-bottom-0">
                    {[
                      Appointment.NA,
                      Appointment.THREE_MONTHS,
                      Appointment.SIX_MONTHS,
                      Appointment.TWELVE_MONTHS,
                      Appointment.TWENTY_FOUR_MONTHS,
                      Appointment.THIRTY_SIX_MONTHS,
                      Appointment.REFERRAL,
                    ].map((value) => (
                      <Form.Check
                        type="radio"
                        key={value}
                        id={`appointment-${value}`}
                        label={tCommon(`reportEditor.appointment.${value}`)}
                        value={value}
                        className={
                          appointment === value
                            ? selectedClass
                            : unselectedClass
                        }
                        {...register('appointment')}
                      />
                    ))}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={
            Object.keys(dirtyFields).length === 0 || isSubmitting || !isValid
          }
        >
          <FontAwesomeIcon icon={faSave} className="me-1"></FontAwesomeIcon>
          {isSubmitting
            ? tCommon('reportEditor.saving')
            : tCommon('reportEditor.save')}
        </Button>

        <Button
          variant="outline-primary"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {tCommon('patientFields.cancel')}
        </Button>
      </Modal.Footer>
    </CustomModal>
  );
};

export default ReportEditor;
